'use client';

import { startingCompanyContext } from '@/@connect/company-context';
import { UseCaseQuestions } from '@/@connect/components/use-case-questions';
import { useCompanyContext } from '@/@connect/hooks/useCompanyContext';
import { clearPendingUseCase, markUseCasePending } from '@/@connect/conversation-use-case';
import { useConnectUseCaseParam } from '@/@connect/hooks/useConnectUseCaseParam';
import { markNextStartEntry } from '@/@studio-ai/utils/first-data-answer';
import { type Answers, contextFromAnswers } from '@/@connect/questions';
import { consumeInsightLaunch, INSIGHT_LAUNCH_QUERY_VALUE } from '@/@discover/insightLaunch';
import { useFeatureFlag } from '@/@feature-flags';
import {
    REPORTS_LAUNCH_QUERY_PARAM,
    REPORTS_LAUNCH_QUERY_VALUE,
    consumeReportsLaunch,
    launchMatchesViewAs,
} from '@/@reports/utils/kickoff';
import { STUDIO_NAV_FEATURE_FLAGS } from '@/@studio/types/constants';
import { getViewAsSnapshot } from '@/@subsidiary/view-as-store';
import { StudioRoutes } from '@/app/routes';
import DropdownTriggerButton from '@/components/ui/molecules/DropdownTriggerButton';
import {
    Button,
    Chip,
    Image,
    Listbox,
    ListboxItem,
    Popover,
    PopoverContent,
    PopoverTrigger,
    Skeleton,
    useDisclosure,
} from '@heroui/react';
import { Icon } from '@iconify/react';
import { motion } from 'framer-motion';
import NextImage from 'next/image';
import { useSearchParams } from 'next/navigation';
import { useEffect, useMemo, useRef } from 'react';
import { toast } from 'react-toastify';
import {
    emailLinkConsumedEventName,
    trackInsightEmailDeepLink,
} from '@/@studio-ai/tracking/email-deep-link-events';
import { useState } from 'react';
import { CHAT_INPUT_MESSAGES } from '../constants';
import { useStudioAI, useStudioAIViewAs } from '../context';
import { useChatPageCommon } from '../hooks/useChatPageCommon';
import { useDaniDeepLinkPrompt } from '../hooks/useDaniDeepLinkPrompt';
import { usePlanTemplates } from '../hooks/usePlanTemplates';
import { useReleaseNotes } from '../hooks/useReleaseNotes';
import type { ChatLandingProps } from '../props';
import type { ChatMessage, InsightTemplate } from '../types';
import { usePendingVideoStudyFromUrl, VideoStudyChip } from '../reels/components/VideoStudyChip';
import { ChatInput } from './chat-input';

/** The composer's starting text when the chat is opened from a study's videos. */
const VIDEO_STUDY_PREFILL = 'What did people say in these videos?';
import { ClientMasquerade } from './client-masquerade';
import { DarkTooltip } from './dark-tooltip';
import { FreemiumLimitModal } from './freemium-limit-modal';
import { StudioAICreditBanners } from './studio-ai-credit-banners';
import { TemplateCards } from './planner-mode/template-cards';
import { DataSetUpdatesTooltipContent } from './refresh-data-dates';
import { spotlightInsightHref, useSpotlightInsights } from '@/@discover/unknownUserSpotlight';

// Temporarily hides the Insight Template cards row — see the note at its
// render site below. Not deleted; the "Insight Templates" dropdown above
// covers the same selection flow in the meantime.
const SHOW_TEMPLATE_CARDS = false;

/** Header row (icon + label + close button) for the dropdown panels below. */
function DropdownPanelHeader({
    icon,
    label,
    onClose,
}: {
    icon: string;
    label: string;
    onClose: () => void;
}) {
    return (
        <div className="flex items-center justify-between px-4 py-3">
            <div className="flex items-center gap-2 text-[#7B7974]">
                <Icon icon={icon} width={16} height={16} />
                <span className="text-[12px] font-medium">{label}</span>
            </div>
            <button
                type="button"
                onClick={onClose}
                aria-label={`Close ${label}`}
                className="text-default-400 hover:text-default-700"
            >
                <Icon icon="lucide:x" width={16} height={16} />
            </button>
        </div>
    );
}

/**
 * Landing page for /dani — shown when there is no active conversation.
 * Displays the logo, centered chat input, and action buttons.
 * On first message, the context auto-navigates to /dani/[conversationId].
 */
export function ChatLanding({ className = '' }: ChatLandingProps) {
    const {
        router,
        isUnaffiliated,
        isFreemium,
        isInternal,
        dataRefreshDates,
        masqueradeClient,
        setMasqueradeClient,
        isMasqueradeLocked,
        isGlobalViewAsActive,
        limitModalOpen,
        setLimitModalOpen,
        liveUsage,
        sharedChatInputProps,
        handleTemplateSelect,
        session,
    } = useChatPageCommon();

    // Insight-engine email deep link: ?prompt= auto-submits into DANI the
    // moment a signed-in recipient lands here. When DANI is credit-paused the
    // prompt falls back to pre-filling the input instead of sending.
    const deepLink = useDaniDeepLinkPrompt(Boolean(session?.user));
    const deepLinkPaused = sharedChatInputProps.isCreditGatePaused;
    const deepLinkAutoSentRef = useRef(false);
    const { onSendMessage, isLoading: chatBusy } = sharedChatInputProps;
    useEffect(() => {
        if (!deepLink || deepLinkAutoSentRef.current || chatBusy) return;
        deepLinkAutoSentRef.current = true;
        const autoSubmitted = !deepLinkPaused;
        trackInsightEmailDeepLink({
            // Onboarding and insight-engine links land on the same route via the
            // same hook; split the event so the two funnels stay separable.
            name: emailLinkConsumedEventName(deepLink.utmSource),
            props: {
                utmSource: deepLink.utmSource,
                utmCampaign: deepLink.utmCampaign,
                utmContent: deepLink.utmContent,
                promptLength: deepLink.prompt.length,
                autoSubmitted,
            },
        });
        if (autoSubmitted) {
            // A link wrote this question, not the person: not a typed start.
            markNextStartEntry('other');
            void onSendMessage(deepLink.prompt);
        }
    }, [deepLink, deepLinkPaused, chatBusy, onSendMessage]);

    // Connect use case: ?usecase= opens DANI's plan screen for it in place of
    // this landing page. Finishing the questions asks DANI (or fills the input
    // while credits are paused) and remembers the brand, competitors and
    // markets for the org; "Edit the wording" leaves the question in the input.
    //
    // While staff View-as (`masqueradeClient`) is active, the plan screen
    // defaults to the viewed-as org's name instead of the real session org —
    // this is how internal users preview the feature for another
    // organization. Its saved brand/competitors/markets never load or save
    // against the real org's row while masquerading (the API is intentionally
    // locked to the real session org — see `company-context/route.ts`), so
    // masquerading always starts the questions fresh, matching what a
    // brand-new org would actually see.
    const connectUseCase = useConnectUseCaseParam(Boolean(session?.user));
    const companyContext = useCompanyContext(Boolean(connectUseCase) && !masqueradeClient);
    const orgName = masqueradeClient?.name ?? session?.org?.orgName ?? '';
    const connectContext = startingCompanyContext(companyContext.saved);
    const [connectClosed, setConnectClosed] = useState(false);
    const [connectPrefill, setConnectPrefill] = useState<string>();
    // Set only on the "ask DANI now" path below, never the paused-prefill
    // one — `onSendMessage` sends synchronously but its `router.push` to
    // `/dani/[conversationId]` is a transition, so this component keeps
    // rendering for a tick after `connectClosed` flips. Without this, that
    // tick shows the full landing (logo, empty input, action buttons) in
    // between the plan screen and the conversation it just started.
    const [connectSending, setConnectSending] = useState(false);

    const finishConnectQuestions = (prompt: string, answers: Answers) => {
        if (!connectUseCase) return;
        setConnectClosed(true);
        // The conversation this starts can offer to run the use case again.
        markUseCasePending(connectUseCase.id);
        // Never while masquerading — this would otherwise save straight to
        // the real session org's row under whichever org's name is showing.
        if (!masqueradeClient) {
            void companyContext
                .save(contextFromAnswers(connectContext, connectUseCase.template, answers))
                .catch((error) => console.warn('[connect] Remembering answers failed:', error));
        }
        if (deepLinkPaused) {
            setConnectPrefill(prompt);
        } else {
            setConnectSending(true);
            void onSendMessage(prompt);
        }
    };

    const insightTemplatesDisclosure = useDisclosure();
    const spotlightFindingsDisclosure = useDisclosure();
    // This week's Spotlight findings for the current user — their resolved
    // ICP's batch for external users, the generic sample otherwise — read
    // from the cache Spotlight itself writes. Empty until Spotlight has
    // cached them — see the note on the dropdown's render condition below.
    const spotlightInsights = useSpotlightInsights();

    // Shows a preview of what selecting a hovered dropdown item would do, in
    // place of the input's normal placeholder. Cleared on mouse-leave and
    // whenever either dropdown closes.
    const [hoveredHint, setHoveredHint] = useState<string | null>(null);

    // Same gate as the masquerade picker's one existing render site below —
    // shared so the plan screen's copy can never show it when the landing
    // page itself wouldn't. Staff with the View-as control never get the
    // DANI-only picker: they act as a client only through a recorded View-as
    // session (M4S-5670 §4). Other internal staff keep it (D9).
    const { hasViewAsControl, plannerAllowedForClient } = useStudioAIViewAs();
    const canMasquerade = isInternal && !isFreemium && !isGlobalViewAsActive && !hasViewAsControl;

    const canViewReleaseNotes = !isUnaffiliated && !isFreemium;
    const { pendingNotes } = useReleaseNotes(canViewReleaseNotes);
    const pendingCount = pendingNotes.length;

    const isPlannerModeEnabled = useFeatureFlag('studio-ai-planner-mode');
    // Plan templates are hidden under the DANI-only picker. Under global
    // View-as they show only when reports may run as a client
    // (`isPlannerAllowedForClient`, M4S-5670 §4).
    const isTemplatesVisible = isGlobalViewAsActive
        ? plannerAllowedForClient
        : !masqueradeClient && !isFreemium && isPlannerModeEnabled;

    // The Spotlight Insights dropdown follows the Spotlight nav flag the same
    // way Report Templates follows the Reports flag: turning Spotlight off
    // hides it here, in the leftnav, and on the /discover/insights route.
    const isSpotlightNavEnabled = useFeatureFlag(STUDIO_NAV_FEATURE_FLAGS.SPOTLIGHT);

    // Reports build-form handoff: /dani?launch=reports carries a sessionStorage
    // payload (templateId + composed INPUTS prompt) that kicks off the template
    // here, inside StudioAIProvider — the Reports page itself can't stream.
    const searchParams = useSearchParams();
    const fromVideos = usePendingVideoStudyFromUrl();
    const launchParam = searchParams.get(REPORTS_LAUNCH_QUERY_PARAM);
    const hasConsumedLaunch = useRef(false);
    useEffect(() => {
        if (hasConsumedLaunch.current) return;
        if (launchParam !== REPORTS_LAUNCH_QUERY_VALUE) return;
        // Wait for flags to load / respect the same guards as the template cards.
        if (!isTemplatesVisible) return;
        hasConsumedLaunch.current = true;
        const payload = consumeReportsLaunch();
        if (!payload) {
            router.replace(StudioRoutes.STUDIO_AI);
            return;
        }
        // A report set up while viewing as one client (or as the staff member)
        // is never launched as another (M4S-5670 §7). The payload is already
        // consumed, so it is dropped.
        if (!launchMatchesViewAs(payload, getViewAsSnapshot().sfAccountId)) {
            toast.error(
                'This report was set up while viewing as a different client. Start it again.',
            );
            router.replace(StudioRoutes.STUDIO_AI);
            return;
        }
        void handleTemplateSelect({
            id: payload.templateId,
            title: payload.templateTitle,
            prompt: payload.prompt,
            reportTitle: payload.reportTitle,
        });
    }, [launchParam, isTemplatesVisible, handleTemplateSelect, router]);

    // An insight card's Dive deep: /dani?launch=insight carries the card's
    // finding in sessionStorage (`@/@discover/insightLaunch`). It opens as a new
    // chat that hands the finding to DANI with the first question, with the
    // card's follow-up question in the composer, and is named after the card's
    // headline once DANI saves it (docs/pdlc/insights-dive-deep.md).
    const { seedConversation } = useStudioAI();
    const hasConsumedInsight = useRef(false);
    useEffect(() => {
        if (hasConsumedInsight.current) return;
        if (launchParam !== INSIGHT_LAUNCH_QUERY_VALUE) return;
        hasConsumedInsight.current = true;
        const launch = consumeInsightLaunch();
        if (!launch) {
            router.replace(StudioRoutes.STUDIO_AI);
            return;
        }
        const messages: ChatMessage[] = launch.messages.map((m) => ({
            ...m,
            content: m.content as ChatMessage['content'],
            timestamp: new Date(m.timestamp),
        }));
        seedConversation(messages, { draft: launch.followUp, title: launch.title });
    }, [launchParam, seedConversation, router]);

    // Live plan-template rows feed the Report Templates dropdown below (and
    // the hidden template cards). Already filtered per-user by featureFlagKey
    // inside usePlanTemplates.
    const { data: rawTemplates, isLoading: templatesLoading } = usePlanTemplates({
        enabled: isTemplatesVisible,
    });

    const templates: InsightTemplate[] = useMemo(
        () =>
            (rawTemplates ?? []).map((t) => ({
                id: t.id,
                imageDataUri: t.imageDataUri,
                title: t.name,
                subtitle: t.description || '',
                category: t.category ?? null,
            })),
        [rawTemplates],
    );

    // Arriving from a Connect use case: its plan screen, not the landing page.
    // Blank while the remembered answers load, so the questions start once.
    if (connectUseCase && !connectClosed) {
        if (companyContext.isLoading) return <div className={`h-full bg-[#FAFAFA] ${className}`} />;
        return (
            <div className={`h-full ${className}`}>
                <UseCaseQuestions
                    // Remounts (discarding in-progress answers) when the
                    // viewed-as identity changes, so switching org mid-screen
                    // restarts the brand default and recommendations instead
                    // of leaving stale answers from the previous one.
                    key={masqueradeClient?.id ?? 'self'}
                    useCase={connectUseCase}
                    context={connectContext}
                    orgName={orgName}
                    onSubmit={finishConnectQuestions}
                    onDismiss={(prompt) => {
                        clearPendingUseCase();
                        setConnectClosed(true);
                        setConnectPrefill(prompt);
                    }}
                    orgSwitcher={
                        canMasquerade ? (
                            <ClientMasquerade
                                selectedClient={masqueradeClient}
                                onSelectClient={setMasqueradeClient}
                                isLocked={isMasqueradeLocked}
                            />
                        ) : undefined
                    }
                />
            </div>
        );
    }
    // The plan screen just closed on the "ask DANI now" path — its
    // `router.push` to `/dani/[conversationId]` is a transition, so this
    // component keeps rendering for a tick after `connectClosed` flips.
    // Stay blank rather than flashing the full landing in between.
    if (connectSending) return <div className={`h-full bg-[#FAFAFA] ${className}`} />;

    // Reports build-form handoff (/dani?launch=reports): the effect above
    // consumes the payload and `router.push`es to /dani/[conversationId] once
    // flags have loaded. Until then — including the flag-loading wait — stay
    // blank rather than flashing the full landing composer before the plan/
    // progress view appears. Error paths clear `?launch=` via router.replace,
    // which releases this guard.
    if (launchParam === REPORTS_LAUNCH_QUERY_VALUE)
        return <div className={`h-full bg-[#FAFAFA] ${className}`} />;

    return (
        <div className={`relative flex h-full bg-[#FAFAFA] ${className}`}>
            {isFreemium && (
                <FreemiumLimitModal
                    isOpen={limitModalOpen}
                    onOpenChange={setLimitModalOpen}
                    liveUsage={liveUsage}
                />
            )}

            {!isUnaffiliated && (
                <motion.div
                    initial={{ opacity: 0, y: -8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: 0.2 }}
                    className="absolute right-12 top-8 z-30 flex items-center gap-2"
                >
                    {canViewReleaseNotes && (
                        <DarkTooltip content="View release notes" size="sm">
                            <div className="relative">
                                <Button
                                    isIconOnly
                                    aria-label="View release notes"
                                    size="sm"
                                    variant="flat"
                                    className="h-9 w-9 rounded-full bg-default-100 text-default-500 data-[hover=true]:bg-default-200 data-[hover=true]:text-default-700"
                                    onPress={() =>
                                        router.push(StudioRoutes.STUDIO_AI_RELEASE_NOTES)
                                    }
                                >
                                    <Icon icon="lucide:bell" width={18} height={18} />
                                </Button>
                                {pendingCount > 0 && (
                                    <span className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-danger text-[10px] font-semibold text-white">
                                        {pendingCount}
                                    </span>
                                )}
                            </div>
                        </DarkTooltip>
                    )}
                    {canMasquerade && (
                        <ClientMasquerade
                            selectedClient={masqueradeClient}
                            onSelectClient={setMasqueradeClient}
                            isLocked={isMasqueradeLocked}
                        />
                    )}
                    {/* Not wired up to a click action yet. */}
                    <DarkTooltip
                        content={
                            dataRefreshDates ? (
                                <DataSetUpdatesTooltipContent dataRefreshDates={dataRefreshDates} />
                            ) : (
                                'Coming soon'
                            )
                        }
                        placement="bottom"
                        size="sm"
                        classNames={{ content: 'shadow-large rounded-md p-2 my-4' }}
                    >
                        <Button
                            isIconOnly
                            aria-label="Clock"
                            size="sm"
                            variant="flat"
                            className="h-9 w-9 rounded-full bg-default-100 text-default-500 data-[hover=true]:bg-default-200 data-[hover=true]:text-default-700"
                        >
                            <Icon icon="lucide:clock" width={18} height={18} />
                        </Button>
                    </DarkTooltip>
                    <DarkTooltip content="Manage your conversation history" size="sm">
                        <Button
                            aria-label="Manage conversation history"
                            size="sm"
                            variant="solid"
                            color="primary"
                            className="h-9 w-24 rounded-large text-[13px] font-medium"
                            onPress={() => router.push(StudioRoutes.STUDIO_AI_HISTORY)}
                        >
                            Chat history
                        </Button>
                    </DarkTooltip>
                </motion.div>
            )}

            <div className="flex min-h-0 min-w-0 flex-1 flex-col">
                <div className="flex flex-1 flex-col items-center justify-start px-4 pt-28">
                    <Image
                        src="/assets/images/MFourLogo.svg"
                        as={NextImage}
                        radius="none"
                        alt="logo"
                        width={100}
                        height={100}
                    />
                    <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4 }}
                        className="mb-8 mt-4 flex flex-col items-center gap-2"
                    >
                        <span className="font-display items-center text-medium font-bold tracking-tight text-foreground">
                            {CHAT_INPUT_MESSAGES.MAIN}{' '}
                        </span>
                        <span className="font-display items-center text-small font-semibold tracking-tight text-default-500">
                            {CHAT_INPUT_MESSAGES.MAIN_SUBTITLE}{' '}
                            {isFreemium ? (
                                <Chip variant="flat" color="warning" size="sm">
                                    Freemium
                                </Chip>
                            ) : null}
                        </span>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4, delay: 0.1 }}
                        className="w-full max-w-3xl space-y-2"
                    >
                        <StudioAICreditBanners />
                        <VideoStudyChip conversationId={null} />
                        <ChatInput
                            {...sharedChatInputProps}
                            initialMessage={
                                connectPrefill ??
                                (deepLink && deepLinkPaused ? deepLink.prompt : undefined) ??
                                (fromVideos ? VIDEO_STUDY_PREFILL : undefined)
                            }
                            previewText={hoveredHint}
                            showScrollButton={false}
                        />
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4, delay: 0.15 }}
                        className="mt-3 flex w-[675px] items-center gap-2"
                    >
                        {/* Only rendered once Spotlight has actually generated
                            this week's batch. Generation is a live, per-user-billed
                            multi-minute DANI call, so this page will not kick one
                            off just to populate a dropdown — and showing invented
                            entries is what got this control hidden in the first
                            place. No findings cached yet => no dropdown. */}
                        {isSpotlightNavEnabled && spotlightInsights.length > 0 && (
                            <Popover
                                placement="bottom-start"
                                shouldFlip={false}
                                offset={-32}
                                isOpen={spotlightFindingsDisclosure.isOpen}
                                onOpenChange={(isOpen) => {
                                    spotlightFindingsDisclosure.onOpenChange();
                                    if (!isOpen) setHoveredHint(null);
                                }}
                            >
                                <PopoverTrigger>
                                    <DropdownTriggerButton
                                        className="!min-w-0 !bg-transparent !px-2 !py-1 text-[14px] font-normal text-default-600 hover:!bg-default-100"
                                        endContent={
                                            <Icon
                                                icon="lucide:chevron-down"
                                                width={14}
                                                height={14}
                                            />
                                        }
                                    >
                                        Spotlight Insights
                                    </DropdownTriggerButton>
                                </PopoverTrigger>
                                <PopoverContent className="w-[675px] items-stretch overflow-hidden rounded-2xl p-0">
                                    <DropdownPanelHeader
                                        icon="lucide:lightbulb"
                                        label="Spotlight Insights"
                                        onClose={spotlightFindingsDisclosure.onClose}
                                    />
                                    <Listbox
                                        aria-label="Spotlight Insights"
                                        className="px-0"
                                        classNames={{ list: 'gap-0 divide-y divide-default-100' }}
                                        itemClasses={{
                                            base: 'rounded-none px-4 py-2 data-[hover=true]:bg-default-100 overflow-hidden',
                                            // With a description, HeroUI puts title+description in
                                            // a flex wrapper whose min-width defaults to its content
                                            // — it overflows instead of shrinking, so the truncate
                                            // span below it never gets squeezed. min-w-0 down the
                                            // chain lets the ellipsis actually engage.
                                            wrapper: 'min-w-0 max-w-full',
                                            title: 'text-[14px] text-[#373734] min-w-0 max-w-full',
                                        }}
                                        onAction={(key) => {
                                            spotlightFindingsDisclosure.onClose();
                                            setHoveredHint(null);
                                            router.push(spotlightInsightHref(String(key)));
                                        }}
                                    >
                                        {spotlightInsights.map((insight) => (
                                            <ListboxItem
                                                key={insight.id}
                                                textValue={insight.headline}
                                                description={insight.tag}
                                                onMouseEnter={() =>
                                                    setHoveredHint(
                                                        `Open the "${insight.headline}" finding in Spotlight.`,
                                                    )
                                                }
                                                onMouseLeave={() => setHoveredHint(null)}
                                            >
                                                {/* Headlines can outrun the 675px panel — clip
                                                    with an ellipsis and surface the full text on
                                                    hover instead of letting it overflow. */}
                                                <DarkTooltip
                                                    content={insight.headline}
                                                    placement="top"
                                                    size="sm"
                                                    classNames={{ content: 'max-w-[560px]' }}
                                                >
                                                    <span className="block max-w-full truncate">
                                                        {insight.headline}
                                                    </span>
                                                </DarkTooltip>
                                            </ListboxItem>
                                        ))}
                                    </Listbox>
                                </PopoverContent>
                            </Popover>
                        )}

                        {isTemplatesVisible && (templates.length > 0 || templatesLoading) && (
                            <Popover
                                placement="bottom-start"
                                shouldFlip={false}
                                offset={-32}
                                isOpen={insightTemplatesDisclosure.isOpen}
                                onOpenChange={(isOpen) => {
                                    insightTemplatesDisclosure.onOpenChange();
                                    if (!isOpen) setHoveredHint(null);
                                }}
                            >
                                <PopoverTrigger>
                                    <DropdownTriggerButton
                                        className="!min-w-0 !bg-transparent !px-2 !py-1 text-[14px] font-normal text-default-600 hover:!bg-default-100"
                                        endContent={
                                            <Icon
                                                icon="lucide:chevron-down"
                                                width={14}
                                                height={14}
                                            />
                                        }
                                    >
                                        Report Templates
                                    </DropdownTriggerButton>
                                </PopoverTrigger>
                                <PopoverContent className="w-[675px] items-stretch overflow-hidden rounded-2xl p-0">
                                    <DropdownPanelHeader
                                        icon="lucide:file-text"
                                        label="Report Templates"
                                        onClose={insightTemplatesDisclosure.onClose}
                                    />
                                    <Listbox
                                        aria-label="Report Templates"
                                        className="max-h-[320px] overflow-y-auto px-0"
                                        classNames={{ list: 'gap-0 divide-y divide-default-100' }}
                                        itemClasses={{
                                            base: 'rounded-none px-4 py-2 data-[hover=true]:bg-default-100',
                                            title: 'text-[14px] text-[#373734]',
                                        }}
                                        items={templates}
                                        onAction={(key) => {
                                            insightTemplatesDisclosure.onClose();
                                            setHoveredHint(null);
                                            const template = templates.find(
                                                (t) => String(t.id) === String(key),
                                            );
                                            if (template) {
                                                void handleTemplateSelect({
                                                    id: template.id,
                                                    title: template.title,
                                                });
                                            }
                                        }}
                                    >
                                        {(template) => (
                                            <ListboxItem
                                                key={template.id}
                                                textValue={template.title}
                                                description={template.subtitle || undefined}
                                                onMouseEnter={() =>
                                                    setHoveredHint(
                                                        `Start the "${template.title}" report in DANI.`,
                                                    )
                                                }
                                                onMouseLeave={() => setHoveredHint(null)}
                                            >
                                                {template.title}
                                            </ListboxItem>
                                        )}
                                    </Listbox>
                                </PopoverContent>
                            </Popover>
                        )}
                    </motion.div>

                    {SHOW_TEMPLATE_CARDS && isTemplatesVisible && (
                        <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.4, delay: 0.25 }}
                            className="mt-4 flex w-full max-w-3xl flex-col gap-2"
                        >
                            {templatesLoading ? (
                                <div className="grid w-full grid-cols-3 gap-3">
                                    {Array.from({ length: 3 }).map((_, i) => (
                                        <Skeleton key={i} className="h-[88px] rounded-lg" />
                                    ))}
                                </div>
                            ) : (
                                <TemplateCards
                                    templates={templates}
                                    onSelectTemplate={async (template: InsightTemplate) =>
                                        handleTemplateSelect({
                                            id: template.id,
                                            title: template.title,
                                        })
                                    }
                                />
                            )}
                        </motion.div>
                    )}
                </div>
            </div>
        </div>
    );
}
